import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Activity, Mail } from 'lucide-react';
import PageMeta from '../components/PageMeta';

/**
 * Public Postmortems index — RFC-style incident write-ups.
 *
 * Today the list is empty (we have not had any SLO-breaching incidents on
 * production hibff.com). When one happens, we drop a markdown file or DB
 * entry here and it auto-renders. B2B procurement loves to see this page exist
 * — it signals operational maturity and openness, even before any incident.
 */

const POSTMORTEMS = [
  // Empty until we have a real incident. Shape:
  // {
  //   id: 'pm-2026-04-25-db-failover',
  //   title: 'MongoDB Atlas primary failover (12 min impact)',
  //   date: '2026-04-25',
  //   severity: 'minor',     // minor | major | critical
  //   summary: '12-minute read-only window during automated Atlas primary failover…',
  //   body: '<long markdown rendered as HTML>',
  // },
];

const PostmortemsPage = () => (
  <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="postmortems-page">
    <PageMeta
      slug="postmortems"
      path="/postmortems"
      title="Public postmortems"
      description="When something goes wrong, we publish the postmortem here within 7 business days — what happened, when, root cause, fix, and follow-up actions."
    />
    <header className="px-6 sm:px-12 py-6 max-w-4xl mx-auto">
      <Link to="/trust-center" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="postmortems-back">
        <ArrowLeft size={18} /> <span className="font-medium">Trust Center</span>
      </Link>
    </header>

    <main className="px-6 sm:px-12 max-w-4xl mx-auto pb-20">
      <div className="flex items-center gap-3 mb-2 text-purple-600">
        <FileText size={28} />
        <span className="font-semibold text-sm uppercase tracking-wide">Postmortems</span>
      </div>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-3" style={{ color: '#1A1209' }}>
        Public postmortems
      </h1>
      <p className="text-base text-gray-700 mb-8 max-w-2xl">
        When something goes wrong, we publish the postmortem here within{' '}
        <strong>7 business days</strong>. No spin, no PR-speak — what happened,
        when, root cause, fix, follow-ups, and process changes.
      </p>

      {POSTMORTEMS.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-12 sm:px-10 sm:py-16 text-center" data-testid="postmortems-empty">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <Activity size={28} className="text-emerald-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-2" style={{ color: '#1A1209' }}>
            No postmortems yet ✨
          </h2>
          <p className="text-gray-600 max-w-lg mx-auto">
            We&apos;ve not had a service-level breach on production hibff.com to write
            up. The day we do, you&apos;ll find the full forensic write-up here.
          </p>
          <Link
            to="/status"
            className="inline-flex items-center gap-2 mt-6 px-5 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition text-sm font-semibold"
            data-testid="postmortems-status-link"
          >
            <Activity size={14} /> Live status
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="postmortems-list">
          {POSTMORTEMS.map(pm => (
            <article key={pm.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold ${
                  pm.severity === 'critical' ? 'bg-red-100 text-red-700'
                  : pm.severity === 'major' ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-700'
                }`}>{pm.severity}</span>
                <span className="text-xs text-gray-500">{pm.date}</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{pm.title}</h3>
              <p className="text-sm text-gray-600">{pm.summary}</p>
            </article>
          ))}
        </div>
      )}

      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-6 text-center mt-10">
        <h2 className="font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
          <Mail size={16} /> Spotted something off but not in our incidents?
        </h2>
        <p className="text-sm text-gray-700 mb-3">
          Tell us at{' '}
          <a href="mailto:security@hibff.com" className="text-purple-600 hover:underline">security@hibff.com</a>.
          We respond within 2 business days.
        </p>
      </div>
    </main>
  </div>
);

export default PostmortemsPage;
